/**
 * Invited Onboarding Page
 *
 * Simplified 2-step onboarding flow for users who join via invitation.
 * Step 1: Profile setup (name only)
 * Step 2: Welcome message with capabilities
 *
 * @module pages/InvitedOnboardingPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { db } from '@/lib/firebase';
import { InvitedProfileStep, InvitedWelcomeStep } from '@/components/onboarding';
import { PageSpinner, Card } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

type OnboardingStep = 'profile' | 'welcome';

// =============================================================================
// Component
// =============================================================================

export function InvitedOnboardingPage() {
  usePageTitle('Welcome');

  const navigate = useNavigate();
  const { firebaseUser, user, organization, loading: authLoading } = useAuth();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Redirect Logic
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // If user has already completed onboarding, redirect to dashboard
    if (!authLoading && user?.onboardingCompletedAt) {
      navigate('/overview', { replace: true });
    }
  }, [authLoading, user?.onboardingCompletedAt, navigate]);

  // If user already has firstName set, skip to welcome step
  useEffect(() => {
    if (user?.firstName && currentStep === 'profile') {
      setCurrentStep('welcome');
    }
  }, [user?.firstName, currentStep]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleProfileSubmit = useCallback(
    async (data: { firstName: string; lastName: string }) => {
      if (!firebaseUser || !organization) return;

      setLoading(true);
      setError(null);

      try {
        const displayName = `${data.firstName} ${data.lastName}`.trim();
        const now = Timestamp.now();

        // Update user document
        const userRef = doc(db, 'users', firebaseUser.uid);
        await updateDoc(userRef, {
          firstName: data.firstName,
          lastName: data.lastName,
          updatedAt: now,
        });

        // Also update member document in the organization for immediate consistency
        // (The onUserUpdated trigger provides a safety net for future updates)
        const memberRef = doc(db, 'organizations', organization.id, 'members', firebaseUser.uid);
        await updateDoc(memberRef, {
          displayName,
        });

        // User data updates via onSnapshot automatically
        // Move to welcome step
        setCurrentStep('welcome');
      } catch (err) {
        console.error('Failed to save profile:', err);
        setError('Failed to save your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [firebaseUser, organization]
  );

  const handleComplete = useCallback(async () => {
    if (!firebaseUser) return;

    setLoading(true);

    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      await updateDoc(userRef, {
        onboardingCompletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // User data updates via onSnapshot automatically
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      // Continue anyway - the important thing is they're set up
    } finally {
      setLoading(false);
    }
  }, [firebaseUser]);

  // ---------------------------------------------------------------------------
  // Loading State & Auth Check
  // ---------------------------------------------------------------------------

  if (authLoading) {
    return <PageSpinner />;
  }

  if (!firebaseUser) {
    navigate('/login', { replace: true });
    return <PageSpinner />;
  }

  if (!user || !organization) {
    return <PageSpinner />;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="max-w-lg w-full">
        <div className="p-6 sm:p-8">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-brand-500 overflow-hidden">
              <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
                <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-semibold text-lg text-gray-900">
              Hummingbird
            </span>
          </div>

          {/* Step Content */}
          {currentStep === 'profile' && (
            <InvitedProfileStep
              organizationName={organization.name}
              onSubmit={handleProfileSubmit}
              loading={loading}
              error={error}
              initialValues={{
                firstName: user.firstName || '',
                lastName: user.lastName || '',
              }}
            />
          )}

          {currentStep === 'welcome' && (
            <InvitedWelcomeStep
              organizationName={organization.name}
              onComplete={handleComplete}
            />
          )}
        </div>
      </Card>
    </div>
  );
}
